import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { Task, ProjectInfo, RAPID_ROLES, RASCI_ROLES } from '../types';

export interface ExportData {
  projectInfo: ProjectInfo;
  tasks: Task[];
  people: Array<{ id: string; name: string; category: string }>;
  personNames: { [key: string]: string };
  matrixType?: 'RAPID' | 'RASCI';
}

export const exportToExcel = (data: ExportData, projectName: string) => {
  const roles = data.matrixType === 'RASCI' ? RASCI_ROLES : RAPID_ROLES;
  // Create workbook
  const wb = XLSX.utils.book_new();

  // Prepare data for spreadsheet
  const headers = ['Work Area', 'Task Description'];
  data.people.forEach(person => {
    const displayName = data.personNames[person.id] || person.id;
    headers.push(`${person.id} (${displayName})`);
  });

  const sheetData = [headers];
  
  data.tasks.forEach(task => {
    const row = [task.workArea, task.taskDescription];
    data.people.forEach(person => {
      const assignment = task.assignments[person.id] || '';
      row.push(assignment);
    });
    sheetData.push(row);
  });

  // Create worksheet
  const ws = XLSX.utils.aoa_to_sheet(sheetData);
  
  // Set column widths
  const colWidths = [
    { wch: 20 }, // Work Area
    { wch: 40 }, // Task Description
    ...data.people.map(() => ({ wch: 15 })) // People columns
  ];
  ws['!cols'] = colWidths;

  // Style headers
  const headerRange = XLSX.utils.decode_range(ws['!ref'] || 'A1');
  for (let col = headerRange.s.c; col <= headerRange.e.c; col++) {
    const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
    if (!ws[cellAddress]) continue;
    ws[cellAddress].s = {
      font: { bold: true },
      fill: { bgColor: { indexed: 64 }, fgColor: { rgb: '2c3e50' } }
    };
  }

  XLSX.utils.book_append_sheet(wb, ws, 'RAPID Matrix');

  // Add project info sheet
  const infoData = [
    ['Project Information'],
    ['Date', data.projectInfo.date],
    ['Summary', data.projectInfo.projectSummary],
    [''],
    [data.matrixType === 'RASCI' ? 'RASCI Roles' : 'RAPID Roles'],
    ...roles.map(role => [role.id, role.name, role.description])
  ];
  const infoWs = XLSX.utils.aoa_to_sheet(infoData);
  XLSX.utils.book_append_sheet(wb, infoWs, 'Project Info');

  // Export
  const fileName = `${projectName.replace(/[^a-z0-9]/gi, '_')}_${data.matrixType || 'RAPID'}_Matrix.xlsx`;
  XLSX.writeFile(wb, fileName);
};

export const exportToCSV = (data: ExportData, projectName: string) => {
  const headers = ['Work Area', 'Task Description'];
  data.people.forEach(person => {
    const displayName = data.personNames[person.id] || person.id;
    headers.push(`${person.id} (${displayName})`);
  });

  let csvContent = headers.join(',') + '\n';
  
  data.tasks.forEach(task => {
    const row = [
      `"${task.workArea.replace(/"/g, '""')}"`,
      `"${task.taskDescription.replace(/"/g, '""')}"`
    ];
    data.people.forEach(person => {
      const assignment = task.assignments[person.id] || '';
      row.push(`"${assignment}"`);
    });
    csvContent += row.join(',') + '\n';
  });

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const fileName = `${projectName.replace(/[^a-z0-9]/gi, '_')}_${data.matrixType || 'RAPID'}_Matrix.csv`;
  saveAs(blob, fileName);
};

export const exportToPDF = async (elementId: string, projectName: string, matrixType?: 'RAPID' | 'RASCI') => {
  const element = document.getElementById(elementId);
  if (!element) {
    throw new Error('Element not found for PDF export');
  }

  try {
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      allowTaint: true
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('l', 'mm', 'a4'); // landscape orientation
    
    const imgWidth = 290; // A4 landscape width minus margins
    const pageHeight = 200; // A4 landscape height minus margins
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;

    let position = 10; // Top margin

    // Add title
    pdf.setFontSize(16);
    pdf.text(`RAPID Decision Matrix - ${projectName}`, 10, position);
    position += 10;

    // Add image
    pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    // Add new pages if needed
    while (heightLeft >= 0) {
      position = heightLeft - imgHeight + 10;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    const fileName = `${projectName.replace(/[^a-z0-9]/gi, '_')}_${matrixType || 'RAPID'}_Matrix.pdf`;
    pdf.save(fileName);
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  }
};

export const exportToJSON = (data: ExportData, projectName: string) => {
  const exportData = {
    ...data,
    exportDate: new Date().toISOString(),
    projectName
  };

  const blob = new Blob([JSON.stringify(exportData, null, 2)], { 
    type: 'application/json;charset=utf-8;' 
  });
  const fileName = `${projectName.replace(/[^a-z0-9]/gi, '_')}_${data.matrixType || 'RAPID'}_Matrix.json`;
  saveAs(blob, fileName);
};